import { FRIENDLY_API_ERROR } from "@/lib/api";
import { formatDelta, type Tier } from "@/lib/types";

/** Skeleton block used while data is loading. */
export function LoadingPanel({ label = "Querying follower graph" }: { label?: string }) {
  return (
    <section className="border border-ink bg-bg-panel p-6" aria-busy="true">
      <p className="label flex items-center gap-2">
        <span className="size-1.5 animate-pulse bg-accent" aria-hidden="true" />
        {label}
      </p>
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-[minmax(0,1fr)_9rem]">
        <div className="space-y-3">
          {[92, 64, 78, 46, 70, 55].map((w, i) => (
            <div
              key={i}
              className="h-3 animate-pulse bg-ink/10"
              style={{ width: `${w}%`, animationDelay: `${i * 70}ms` }}
            />
          ))}
        </div>
        <div className="hidden space-y-3 sm:block">
          {[80, 45, 60, 30].map((w, i) => (
            <div key={i} className="space-y-1.5">
              <div className="h-2 w-12 animate-pulse bg-ink/10" />
              <div
                className="h-1.5 animate-pulse bg-accent/25"
                style={{ width: `${w}%` }}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/** Terminal-style broken bot — inline SVG, no asset. */
function BrokenRobot({ className = "" }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 120 120"
      width="112"
      height="112"
      className={className}
      aria-hidden="true"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M60 18 V28 M60 18 L72 10"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="square"
      />
      <circle cx="74" cy="8" r="3.5" fill="#e23a3a" />

      <rect
        x="28"
        y="28"
        width="64"
        height="42"
        stroke="currentColor"
        strokeWidth="2.5"
      />
      <path
        d="M58 28 L52 42 L60 48 L54 58 L64 70"
        stroke="#e23a3a"
        strokeWidth="2"
      />
      <path
        d="M40 44 L48 52 M48 44 L40 52"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="square"
      />
      <rect
        x="68"
        y="44"
        width="12"
        height="8"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path d="M70 48 H80" stroke="#e23a3a" strokeWidth="1.5" />
      <path
        d="M44 62 H52 L56 58 L60 66 L64 58 L68 62 H76"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="square"
        strokeLinejoin="miter"
      />

      <path d="M52 70 V78 H68 V70" stroke="currentColor" strokeWidth="2.5" />
      <rect
        x="34"
        y="78"
        width="52"
        height="28"
        stroke="currentColor"
        strokeWidth="2.5"
      />
      <rect
        x="44"
        y="86"
        width="18"
        height="12"
        stroke="currentColor"
        strokeWidth="1.75"
      />
      <path
        d="M70 88 L76 94 L72 94 L78 102"
        stroke="#e23a3a"
        strokeWidth="2"
        strokeLinecap="square"
      />

      <path
        d="M34 84 H22 V98"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="square"
      />
      <path
        d="M86 84 H98 L104 96"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="square"
        opacity="0.55"
      />

      <circle
        cx="16"
        cy="104"
        r="3"
        stroke="currentColor"
        strokeWidth="1.75"
      />
      <path
        d="M14 104 H18 M16 102 V106"
        stroke="currentColor"
        strokeWidth="1.25"
      />
    </svg>
  );
}

export function ErrorPanel({
  message: _message,
  onRetry,
}: {
  /** Ignored — backend details are never shown to users. */
  message?: string;
  onRetry?: () => void;
}) {
  return (
    <section className="relative z-10 border border-neg bg-bg-panel p-6" role="alert">
      <div className="flex flex-col-reverse items-start gap-5 sm:flex-row sm:justify-between sm:gap-8">
        <div className="min-w-0 flex-1">
          <p className="label !text-neg">Request failed</p>
          <p className="mono mt-3 max-w-[42ch] text-[13px] text-ink-soft">
            {FRIENDLY_API_ERROR}
          </p>
          {onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className="btn btn-dark mt-5"
            >
              Try again →
            </button>
          ) : null}
        </div>
        <div
          className="flex shrink-0 items-center justify-center border border-dashed border-neg/50 bg-bg p-3 text-ink"
          aria-hidden="true"
        >
          <BrokenRobot className="block h-20 w-20 sm:h-28 sm:w-28" />
        </div>
      </div>
    </section>
  );
}

export function EmptyPanel({
  title,
  hint,
}: {
  title: string;
  hint?: string;
}) {
  return (
    <section className="border border-line bg-bg-panel p-6">
      <p className="label">{title}</p>
      {hint ? (
        <p className="mono mt-3 text-[13px] text-ink-muted">{hint}</p>
      ) : null}
    </section>
  );
}

const TIER_STYLE: Record<Tier, string> = {
  elite: "border-ink bg-accent text-accent-ink",
  strong: "border-ink bg-bg text-ink",
  watch: "border-line bg-bg text-ink-soft",
};

export function TierBadge({ tier, label }: { tier: Tier; label?: string }) {
  return (
    <span
      className={`mono inline-flex items-center gap-2 border px-2.5 py-1 text-[11px] uppercase tracking-[0.14em] ${TIER_STYLE[tier]}`}
    >
      {label ?? tier}
    </span>
  );
}

export function Delta({ value, suffix = "" }: { value: number; suffix?: string }) {
  const up = value >= 0;
  return (
    <span
      className={`num text-[12px] font-semibold ${up ? "text-pos" : "text-neg"}`}
    >
      {up ? "▲" : "▼"} {formatDelta(value)}
      {suffix}
    </span>
  );
}
